import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { convex } from '../lib/convex'
import { api } from '../../convex/_generated/api'
import type { Id } from '../../convex/_generated/dataModel'
import logger from '../utils/logger'
import { maybeEncryptForFamily } from '../utils/writeEncryption'
import { useFamilyStore } from './familyStore'

/**
 * Compatibility type matching old Supabase shape.
 * Convex data is mapped to this type when synced from reactive queries.
 */
export interface EventRow {
  id: string
  family_id: string
  child_id: string
  title: string
  notes: string | null
  start_at: string
  end_at: string | null
  all_day: boolean
  location: string | null
  type: 'note' | 'expense' | 'event'
  amount: number | null
  currency: string | null
  metadata: Record<string, any> | null
  created_by: string
  created_at: string
  updated_at: string
}

interface EventsState {
  events: EventRow[]
  isLoading: boolean
  error: string | null

  // Data setter (called from Calendar component's Convex query sync)
  setEvents: (events: EventRow[]) => void
  setIsLoading: (isLoading: boolean) => void

  // Backward-compatible fetchEvents (no-op, data comes from Convex reactive queries)
  fetchEvents: (childId: string, startISO: string, endISO: string) => Promise<void>

  // Mutations — call Convex directly
  addEvent: (payload: Omit<EventRow, 'id' | 'created_at' | 'updated_at'>) => Promise<void>
  updateEvent: (id: string, updates: Partial<EventRow>) => Promise<void>
  deleteEvent: (id: string) => Promise<void>
  clearCache: () => void
}

/**
 * Map Convex event to compatibility shape (snake_case).
 */
export function mapConvexEvent(event: any): EventRow {
  return {
    id: event._id,
    family_id: event.familyId,
    child_id: event.childId,
    title: event.title,
    notes: event.notes ?? null,
    start_at: event.startAt,
    end_at: event.endAt ?? null,
    all_day: event.allDay ?? false,
    location: event.location ?? null,
    type: event.type ?? 'note',
    amount: event.amount ?? null,
    currency: event.currency ?? null,
    metadata: event.metadata ?? null,
    created_by: event.createdBy ?? '',
    created_at: new Date(event._creationTime).toISOString(),
    updated_at: new Date(event._creationTime).toISOString(),
  }
}

export const useEventsStore = create<EventsState>()(
  persist(
    (set, get) => ({
      events: [],
      isLoading: false,
      error: null,

      setEvents: (events) => set({ events, isLoading: false }),
      setIsLoading: (isLoading) => set({ isLoading }),

      // No-op: data comes from Convex reactive queries synced in Calendar component
      fetchEvents: async (_childId, _startISO, _endISO) => {
        logger.debug('fetchEvents() called (no-op, data from Convex reactive queries)')
      },

      addEvent: async (payload) => {
        try {
          const family = useFamilyStore.getState().family
          const title = await maybeEncryptForFamily(payload.title, family)
          const notes = payload.notes
            ? await maybeEncryptForFamily(payload.notes, family)
            : undefined

          await convex.mutation(api.events.mutations.addEvent, {
            childId: payload.child_id as Id<"children">,
            familyId: payload.family_id as Id<"families">,
            title,
            notes,
            startAt: payload.start_at,
            endAt: payload.end_at ?? undefined,
            allDay: payload.all_day,
            location: payload.location ?? undefined,
            type: payload.type || 'note',
            amount: payload.amount ?? undefined,
            currency: payload.currency ?? undefined,
            metadata: payload.metadata ?? undefined,
          })
          // Convex reactive query will auto-update the events list
        } catch (e: any) {
          logger.error('addEvent error:', e)
          set({ error: e.message || 'Error adding event' })
        }
      },

      updateEvent: async (id, updates) => {
        try {
          await convex.mutation(api.events.mutations.updateEvent, {
            eventId: id as Id<"events">,
            title: updates.title,
            notes: updates.notes ?? undefined,
            startAt: updates.start_at,
            endAt: updates.end_at ?? undefined,
            allDay: updates.all_day,
            location: updates.location ?? undefined,
            type: updates.type,
            amount: updates.amount ?? undefined,
            currency: updates.currency ?? undefined,
            metadata: updates.metadata ?? undefined,
          })
        } catch (e: any) {
          logger.error('updateEvent error:', e)
          set({ error: e.message || 'Error updating event' })
        }
      },

      deleteEvent: async (id) => {
        // Optimistic delete
        const removed = get().events.find(ev => ev.id === id) || null
        set(state => ({
          events: state.events.filter(ev => ev.id !== id),
        }))

        try {
          await convex.mutation(api.events.mutations.deleteEvent, {
            eventId: id as Id<"events">,
          })
        } catch (e: any) {
          logger.error('deleteEvent error:', e)
          // Revert optimistic delete
          if (removed) {
            set(state => ({ events: [...state.events, removed] }))
          }
          set({ error: e.message || 'Error deleting event' })
        }
      },

      clearCache: () => set({ events: [], isLoading: false, error: null }),
    }),
    {
      name: '2homes-events-storage',
      partialize: () => ({}),
    }
  )
)

export type { EventRow as CalendarEvent }
